import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldAlert, AlertTriangle, LayoutTemplate, Trash2, ChevronDown, ChevronRight,
  Sliders, Send, X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const WEEKDAYS_PT = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const WEEKDAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ROLE_OPTIONS = [
  { value: "employee", pt: "Integrante", en: "Team member" },
  { value: "manager", pt: "Gestor", en: "Manager" },
  { value: "hr", pt: "RH", en: "HR" },
  { value: "admin", pt: "Admin", en: "Admin" },
];

const SensBadge = ({ sens, t }) => {
  if (sens === "confidential") return <span className="text-[10px] font-bold text-rose-600 bg-rose-50 rounded px-1.5 py-0.5 ml-1">{t("reports.sens_confidential")}</span>;
  if (sens === "restricted") return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 rounded px-1.5 py-0.5 ml-1">{t("reports.sens_restricted")}</span>;
  return null;
};

// Global-filter → per-entity-filter helpers ─────────────────────────────────
// Return the entity field key that matches (or null). We inspect the catalog's
// field definitions so we never invent field names the backend does not know.
const primaryDateFieldOf = (entity) => {
  const dateFields = (entity.fields || []).filter((f) => f.type === "date");
  if (!dateFields.length) return null;
  const priority = ["submitted_at", "created_at", "start_date", "date", "hire_date", "opened_at"];
  for (const k of priority) {
    const hit = dateFields.find((f) => f.key === k);
    if (hit) return hit.key;
  }
  return dateFields[0].key;
};
const hasField = (entity, key) => (entity.fields || []).some((f) => f.key === key);

const ReportComposer = ({ catalog, onCancel, onCreated }) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isHR = ["hr", "admin"].includes(user?.role);
  const L = (o) => (lang === "pt" ? o?.label_pt : o?.label_en) || "";

  // Core state
  const [cfg, setCfg] = useState({}); // entityKey → { fields, mode, group_by, metrics }
  const [expanded, setExpanded] = useState({}); // entityKey → bool
  const [globalFilters, setGlobalFilters] = useState({
    period: { kind: "none", window: "previous_month", from: "", to: "" },
    departments: [], roles: [],
  });
  const [schedule, setSchedule] = useState({ frequency: "once", weekday: 0 });
  const [validity, setValidity] = useState({ type: "once", n: 3 });
  const [title, setTitle] = useState("");
  const [justification, setJustification] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/reports/templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);

  const entities = catalog.entities || [];
  const ent = (key) => entities.find((e) => e.key === key);
  const selectedKeys = Object.keys(cfg);
  const weekdays = lang === "pt" ? WEEKDAYS_PT : WEEKDAYS_EN;

  // Options for the global filters — read straight from the catalog so labels
  // and values are guaranteed to be valid on the backend.
  const departmentOptions = useMemo(() => {
    for (const e of entities) {
      for (const f of e.fields || []) {
        if (f.key === "department" && Array.isArray(f.options) && f.options.length) return f.options;
      }
    }
    return [];
  }, [entities]);

  // ── Entity handlers ────────────────────────────────────────────────────────
  const addEntity = (key) => {
    setCfg((prev) => {
      if (prev[key]) return prev; // already there
      const std = ent(key).fields.filter((f) => f.sensitivity === "standard").map((f) => f.key);
      return { ...prev, [key]: { fields: std, mode: "detail", group_by: null, metrics: [] } };
    });
    setExpanded((prev) => ({ ...prev, [key]: true }));
  };
  const removeEntity = (key) => {
    setCfg((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setExpanded((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };
  const patchEntity = (key, patch) => setCfg((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const toggleField = (ekey, fkey) => {
    const c = cfg[ekey];
    const fields = c.fields.includes(fkey) ? c.fields.filter((f) => f !== fkey) : [...c.fields, fkey];
    const patch = { fields };
    if (c.group_by === fkey && !fields.includes(fkey)) patch.group_by = null;
    patchEntity(ekey, patch);
  };
  const toggleMetric = (ekey, op, field) => {
    const c = cfg[ekey];
    const exists = c.metrics.some((m) => m.op === op && m.field === field);
    const metrics = exists ? c.metrics.filter((m) => !(m.op === op && m.field === field)) : [...c.metrics, { op, field }];
    patchEntity(ekey, { metrics });
  };

  // ── Global filter handlers ─────────────────────────────────────────────────
  const setPeriod = (patch) => setGlobalFilters((p) => ({ ...p, period: { ...p.period, ...patch } }));
  const toggleDept = (id) => setGlobalFilters((p) => ({
    ...p,
    departments: p.departments.includes(id) ? p.departments.filter((x) => x !== id) : [...p.departments, id],
  }));
  const toggleRole = (r) => setGlobalFilters((p) => ({
    ...p,
    roles: p.roles.includes(r) ? p.roles.filter((x) => x !== r) : [...p.roles, r],
  }));

  // ── Templates ─────────────────────────────────────────────────────────────
  const applyTemplate = (tpl) => {
    const next = {};
    const nextExpanded = {};
    (tpl.datasets || []).forEach((ds) => {
      next[ds.entity] = {
        fields: [...(ds.fields || [])],
        mode: ds.mode || "detail",
        group_by: ds.group_by || null,
        metrics: [...(ds.metrics || [])],
      };
      nextExpanded[ds.entity] = true;
    });
    setCfg(next);
    setExpanded(nextExpanded);
    if (!title.trim()) setTitle(tpl.name || "");
    toast.success(t("reports.template_applied"));
  };
  const deleteTemplate = async (tid, e) => {
    e.stopPropagation();
    if (!window.confirm(t("reports.template_delete_confirm"))) return;
    try {
      await api.delete(`/reports/templates/${tid}`);
      setTemplates((prev) => prev.filter((x) => x.id !== tid));
    } catch (err) { toast.error(err?.response?.data?.detail || t("common.error")); }
  };

  // ── Build submit payload ──────────────────────────────────────────────────
  // Global filters get materialised per-entity, only when the entity actually
  // exposes a matching field (department / role / a date field). This keeps
  // the backend contract untouched: still per-dataset filters, no schema change.
  const buildDatasets = () => {
    const gp = globalFilters.period;
    const gd = globalFilters.departments;
    const gr = globalFilters.roles;
    return selectedKeys.map((ekey) => {
      const c = cfg[ekey];
      const e = ent(ekey);
      const filters = [];
      // Period
      const dateKey = primaryDateFieldOf(e);
      if (dateKey && gp.kind === "window") {
        filters.push({ field: dateKey, op: "date_window", window: gp.window });
      } else if (dateKey && gp.kind === "fixed" && (gp.from || gp.to)) {
        filters.push({ field: dateKey, op: "date_between", from: gp.from || null, to: gp.to || null });
      }
      // Departments
      if (gd.length && hasField(e, "department")) {
        filters.push({ field: "department", op: "in", values: gd });
      }
      // Roles
      if (gr.length && hasField(e, "role")) {
        filters.push({ field: "role", op: "in", values: gr });
      }
      return {
        entity: ekey, fields: c.fields, filters,
        mode: c.mode, group_by: c.mode === "summary" ? c.group_by : null,
        metrics: c.mode === "summary" ? c.metrics : [],
      };
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const errors = useMemo(() => {
    const errs = [];
    if (!title.trim()) errs.push(t("reports.err_title_required"));
    if (!selectedKeys.length) errs.push(t("reports.err_entity_required"));
    selectedKeys.forEach((k) => {
      const c = cfg[k];
      const e = ent(k);
      if (!c.fields.length) errs.push(`${L(e)}: ${t("reports.err_no_fields")}`);
      if (c.mode === "summary" && !c.group_by) errs.push(`${L(e)}: ${t("reports.err_no_groupby")}`);
    });
    return errs;
  }, [title, cfg, selectedKeys, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasConfidential = selectedKeys.some((k) =>
    cfg[k].fields.some((fk) => ent(k)?.fields.find((f) => f.key === fk)?.sensitivity === "confidential")
  );

  const submit = async () => {
    if (errors.length) { toast.error(errors[0]); return; }
    setSubmitting(true);
    try {
      await api.post("/reports/requests", {
        title: title.trim(),
        justification: justification.trim(),
        datasets: buildDatasets(),
        schedule,
        validity: schedule.frequency === "once" ? { type: "once", n: null } : validity,
        lang,
      });
      toast.success(t("reports.status_pending_approval"));
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (Array.isArray(msg) ? msg.map((x) => x.msg || JSON.stringify(x)).join(" · ") : t("common.error")));
    } finally { setSubmitting(false); }
  };

  // Active-filter chips summary (top-right of the global filter card)
  const activeFilterCount =
    (globalFilters.period.kind !== "none" ? 1 : 0) +
    globalFilters.departments.length + globalFilters.roles.length;

  return (
    <div className="space-y-5" data-testid="report-composer">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <h2 className="sw-heading text-[20px]">{t("reports.new_request")}</h2>
        <button data-testid="composer-cancel" onClick={onCancel} className="sw-btn-secondary text-[13px] flex items-center gap-1.5">
          <X size={14} /> {t("common.cancel")}
        </button>
      </div>

      {/* Global filters ─────────────────────────────────────────────────── */}
      <div className="sw-card" data-testid="composer-global-filters">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sliders size={16} className="text-[#475569]" />
            <span className="font-semibold text-[14px] text-[#1e293b]">{t("reports.global_filters")}</span>
          </div>
          {activeFilterCount > 0 && (
            <span className="text-[11px] font-semibold text-[#475569] bg-[#f1f5f9] rounded-full px-2 py-0.5" data-testid="composer-active-filter-count">
              {activeFilterCount} {t("reports.applied")}
            </span>
          )}
        </div>
        <p className="text-[12px] text-[#94a3b8] mb-3">{t("reports.global_filters_hint")}</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Period */}
          <div>
            <label className="sw-label">{t("reports.period")}</label>
            <select data-testid="composer-period-kind" className="sw-input"
              value={globalFilters.period.kind === "none" ? "none" : (globalFilters.period.kind === "fixed" ? "fixed" : globalFilters.period.window)}
              onChange={(ev) => {
                const v = ev.target.value;
                if (v === "none") setPeriod({ kind: "none" });
                else if (v === "fixed") setPeriod({ kind: "fixed" });
                else setPeriod({ kind: "window", window: v });
              }}>
              <option value="none">{t("reports.period_none")}</option>
              {(catalog.windows || []).map((w) => <option key={w} value={w}>{t(`reports.window_${w}`)}</option>)}
              <option value="fixed">{t("reports.period_custom")}</option>
            </select>
            {globalFilters.period.kind === "fixed" && (
              <div className="flex gap-2 mt-2">
                <input data-testid="composer-period-from" type="date" className="sw-input flex-1" value={globalFilters.period.from} onChange={(ev) => setPeriod({ from: ev.target.value })} />
                <input data-testid="composer-period-to" type="date" className="sw-input flex-1" value={globalFilters.period.to} onChange={(ev) => setPeriod({ to: ev.target.value })} />
              </div>
            )}
          </div>

          {/* Departments */}
          <div>
            <label className="sw-label">{t("reports.departments")}</label>
            {departmentOptions.length === 0 ? (
              <p className="text-[12px] text-[#94a3b8]">{t("reports.no_departments")}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-[92px] overflow-y-auto">
                {departmentOptions.map((o) => (
                  <button key={String(o.value)} data-testid={`composer-dept-${o.value}`} onClick={() => toggleDept(o.value)}
                    className={`text-[12px] rounded-full px-3 py-1 border transition-all ${globalFilters.departments.includes(o.value) ? "bg-[#475569] text-white border-[#475569]" : "border-[#e2e8f0] text-[#747474] hover:border-[#94a3b8]"}`}>
                    {lang === "pt" ? o.label_pt : o.label_en}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Roles */}
          <div>
            <label className="sw-label">{t("reports.roles")}</label>
            <div className="flex flex-wrap gap-1.5">
              {ROLE_OPTIONS.map((r) => (
                <button key={r.value} data-testid={`composer-role-${r.value}`} onClick={() => toggleRole(r.value)}
                  className={`text-[12px] rounded-full px-3 py-1 border transition-all ${globalFilters.roles.includes(r.value) ? "bg-[#475569] text-white border-[#475569]" : "border-[#e2e8f0] text-[#747474] hover:border-[#94a3b8]"}`}>
                  {lang === "pt" ? r.pt : r.en}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Templates section (collapsible) ────────────────────────────────── */}
      {templates.length > 0 && (
        <div className="sw-card" data-testid="composer-templates-section">
          <button className="w-full flex items-center justify-between" onClick={() => setTemplatesOpen((v) => !v)} data-testid="composer-templates-toggle">
            <span className="flex items-center gap-2 font-semibold text-[14px] text-[#1e293b]">
              <LayoutTemplate size={15} className="text-[#475569]" /> {t("reports.start_from_template")}
              <span className="text-[11px] font-normal text-[#94a3b8]">· {templates.length}</span>
            </span>
            {templatesOpen ? <ChevronDown size={16} className="text-[#94a3b8]" /> : <ChevronRight size={16} className="text-[#94a3b8]" />}
          </button>
          {templatesOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
              {templates.map((tpl) => (
                <button key={tpl.id} data-testid={`composer-template-${tpl.id}`} onClick={() => applyTemplate(tpl)}
                  className="text-left rounded-[12px] border border-[#e2e8f0] bg-white p-3 hover:border-[#475569] transition-all group relative">
                  <div className="font-medium text-[13px] text-[#1e293b] pr-6">{tpl.name}</div>
                  {tpl.description && <div className="text-[11px] text-[#747474] mt-1 line-clamp-2">{tpl.description}</div>}
                  <div className="text-[11px] text-[#94a3b8] mt-1.5">
                    {(tpl.datasets || []).map((d) => L(ent(d.entity)) || d.entity).join(" · ")}
                  </div>
                  {isHR && (
                    <span onClick={(e) => deleteTemplate(tpl.id, e)} data-testid={`composer-template-delete-${tpl.id}`}
                      className="absolute top-2 right-2 text-[#94a3b8] hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={13} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Entities accordion ─────────────────────────────────────────────── */}
      <div className="space-y-2" data-testid="composer-entities">
        {entities.map((e) => {
          const active = !!cfg[e.key];
          const isOpen = active && !!expanded[e.key];
          const c = cfg[e.key];
          const groupables = active ? e.fields.filter((f) => c.fields.includes(f.key) && (f.filter === "enum" || f.type === "string")) : [];
          const numericSelected = active ? e.fields.filter((f) => c.fields.includes(f.key) && f.type === "number") : [];
          return (
            <div key={e.key} className={`rounded-[14px] border ${active ? "border-[#475569] bg-white" : "border-[#e2e8f0] bg-white/70"}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <label className="flex items-center gap-3 cursor-pointer flex-1" data-testid={`composer-entity-toggle-${e.key}`}>
                  <input type="checkbox" checked={active} onChange={() => (active ? removeEntity(e.key) : addEntity(e.key))} className="accent-[#475569] w-4 h-4" />
                  <div>
                    <div className="font-semibold text-[14px] text-[#1e293b]">{L(e)}</div>
                    <div className="text-[12px] text-[#94a3b8]">
                      {active
                        ? `${c.fields.length} ${lang === "pt" ? "colunas" : "columns"} · ${c.mode === "summary" ? t("reports.mode_summary") : t("reports.mode_detail")}`
                        : `${e.fields.length} ${lang === "pt" ? "atributos disponíveis" : "attributes available"}`}
                    </div>
                  </div>
                </label>
                {active && (
                  <button data-testid={`composer-entity-expand-${e.key}`} onClick={() => setExpanded((p) => ({ ...p, [e.key]: !p[e.key] }))}
                    className="p-1 text-[#94a3b8] hover:text-[#475569]">
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                )}
              </div>

              {active && isOpen && (
                <div className="px-4 pb-4 border-t border-[#f1f5f9] pt-3 space-y-4">
                  {/* Mode toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] text-[#94a3b8]">{t("reports.select_fields_hint")}</span>
                    <div className="flex gap-1 bg-[#f1f5f9] rounded-full p-0.5">
                      <button data-testid={`composer-mode-detail-${e.key}`} onClick={() => patchEntity(e.key, { mode: "detail" })}
                        className={`px-3 py-1 rounded-full text-[12px] font-medium ${c.mode === "detail" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
                        {t("reports.mode_detail")}
                      </button>
                      <button data-testid={`composer-mode-summary-${e.key}`} onClick={() => patchEntity(e.key, { mode: "summary" })}
                        className={`px-3 py-1 rounded-full text-[12px] font-medium ${c.mode === "summary" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
                        {t("reports.mode_summary")}
                      </button>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="flex flex-wrap gap-2">
                    {e.fields.map((f) => (
                      <button key={f.key} data-testid={`composer-field-${e.key}-${f.key}`} onClick={() => toggleField(e.key, f.key)}
                        className={`text-[12px] rounded-full px-3 py-1.5 border transition-all ${c.fields.includes(f.key) ? "bg-[#475569] text-white border-[#475569]" : "border-[#e2e8f0] text-[#747474] hover:border-[#94a3b8]"}`}>
                        {L(f)}<SensBadge sens={f.sensitivity} t={t} />
                      </button>
                    ))}
                  </div>

                  {/* Summary options */}
                  {c.mode === "summary" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="sw-label">{t("reports.group_by")} <span className="text-rose-500">*</span></label>
                        <select data-testid={`composer-groupby-${e.key}`} className="sw-input" value={c.group_by || ""}
                          onChange={(ev) => patchEntity(e.key, { group_by: ev.target.value || null })}>
                          <option value="">—</option>
                          {groupables.map((f) => <option key={f.key} value={f.key}>{L(f)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="sw-label">{t("reports.metrics")}</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-[12px] rounded-full px-3 py-1.5 bg-[#f1f5f9] text-[#475569] font-medium">Count ✓</span>
                          {numericSelected.map((f) => (
                            <React.Fragment key={f.key}>
                              <button onClick={() => toggleMetric(e.key, "sum", f.key)}
                                className={`text-[12px] rounded-full px-3 py-1.5 border ${c.metrics.some((m) => m.op === "sum" && m.field === f.key) ? "bg-[#475569] text-white border-[#475569]" : "border-[#e2e8f0] text-[#747474]"}`}>
                                Σ {L(f)}
                              </button>
                              <button onClick={() => toggleMetric(e.key, "avg", f.key)}
                                className={`text-[12px] rounded-full px-3 py-1.5 border ${c.metrics.some((m) => m.op === "avg" && m.field === f.key) ? "bg-[#475569] text-white border-[#475569]" : "border-[#e2e8f0] text-[#747474]"}`}>
                                Ø {L(f)}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Meta + schedule + submit ────────────────────────────────────────── */}
      <div className="sw-card space-y-4" data-testid="composer-meta">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="sw-label">{t("reports.title_label")} <span className="text-rose-500">*</span></label>
            <input data-testid="composer-title" className="sw-input" value={title} onChange={(ev) => setTitle(ev.target.value)} placeholder={t("reports.title_ph")} />
          </div>
          <div>
            <label className="sw-label">{t("reports.frequency")}</label>
            <select data-testid="composer-frequency" className="sw-input" value={schedule.frequency}
              onChange={(ev) => setSchedule((s) => ({ ...s, frequency: ev.target.value }))}>
              {(catalog.frequencies || []).map((fr) => <option key={fr} value={fr}>{t(`reports.freq_${fr}`)}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="sw-label">{t("reports.justification")}</label>
          <textarea data-testid="composer-justification" className="sw-input min-h-[60px]" value={justification} onChange={(ev) => setJustification(ev.target.value)} />
        </div>

        {schedule.frequency === "weekly" && (
          <div className="max-w-[220px]">
            <label className="sw-label">{t("reports.weekday_label")}</label>
            <select data-testid="composer-weekday" className="sw-input" value={schedule.weekday}
              onChange={(ev) => setSchedule((s) => ({ ...s, weekday: Number(ev.target.value) }))}>
              {weekdays.map((w, i) => <option key={i} value={i}>{w}</option>)}
            </select>
          </div>
        )}

        {schedule.frequency !== "once" && (
          <div>
            <label className="sw-label">{t("reports.validity")}</label>
            <div className="flex flex-wrap gap-2 mt-1 items-center">
              {["months", "years", "permanent"].map((v) => (
                <button key={v} data-testid={`composer-validity-${v}`} onClick={() => setValidity((p) => ({ ...p, type: v, n: p.n || 3 }))}
                  className={`rounded-full px-4 py-1.5 text-[12px] font-medium border transition-all ${validity.type === v ? "border-[#475569] bg-[#f8fafc] text-[#1e293b]" : "border-[#e2e8f0] text-[#747474]"}`}>
                  {t(`reports.validity_${v}`)}
                </button>
              ))}
              {(validity.type === "months" || validity.type === "years") && (
                <input type="number" min="1" max="60" data-testid="composer-validity-n" className="sw-input max-w-[100px]"
                  value={validity.n || ""} onChange={(ev) => setValidity((p) => ({ ...p, n: Number(ev.target.value) }))} />
              )}
            </div>
          </div>
        )}

        {hasConfidential && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-[12px] p-3 text-[13px] text-rose-700" data-testid="composer-confidential-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {t("reports.confidential_warning")}
          </div>
        )}

        {errors.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-[12px] p-3 text-[12px] text-amber-800 space-y-0.5" data-testid="composer-errors">
            {errors.map((er, i) => <div key={i}>• {er}</div>)}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-[#f1f5f9]">
          <button data-testid="composer-submit" onClick={submit} disabled={submitting || errors.length > 0}
            className="sw-btn-primary flex items-center gap-2">
            {hasConfidential && <ShieldAlert size={15} />}
            <Send size={14} /> {t("reports.submit_request")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportComposer;
